import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Button, 
  InputBase, 
  IconButton, 
  Typography,
  Avatar,
  Badge,
  Chip,
  Divider,
  CircularProgress,
  Fade,
  Slide,
  Menu,
  MenuItem,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close,
  Send,
  Chat,
  QuestionAnswer,
  Help,
  LocalShipping,
  AttachMoney,
  Schedule,
  LocationOn,
  Phone,
  Email,
  Star,
  ThumbUp,
  ThumbDown,
  Refresh,
  ExpandMore,
  SupportAgent
} from '@mui/icons-material';

const QUICK_ACTIONS = [
  { id: 'pricing', label: 'Get Pricing', icon: <AttachMoney />, query: 'How much does it cost to move?' },
  { id: 'booking', label: 'Book a Move', icon: <Schedule />, query: 'How do I book a moving service?' },
  { id: 'services', label: 'Our Services', icon: <LocalShipping />, query: 'What moving services do you offer?' },
  { id: 'contact', label: 'Contact Us', icon: <Phone />, query: 'How can I contact customer support?' }
];

const KNOWLEDGE_BASE = {
  pricing: {
    keywords: ['cost', 'price', 'pricing', 'expensive', 'cheap', 'rate', 'fee', 'charge'],
    response: "Our pricing varies based on several factors:\n\n• **Distance**: Local vs long-distance moves\n• **Size**: Number of rooms and items\n• **Services**: Basic moving, packing, storage\n• **Time**: Peak vs off-peak seasons\n\nFor an accurate quote, I recommend using our instant quote calculator or speaking with one of our moving specialists. Would you like me to help you get a personalized estimate?"
  },
  booking: {
    keywords: ['book', 'schedule', 'appointment', 'reserve', 'when', 'availability'],
    response: "Booking your move is easy! Here's how:\n\n1. **Get a Quote**: Use our online calculator or call us\n2. **Choose Your Date**: We'll check availability\n3. **Confirm Details**: Review services and pricing\n4. **Secure Your Booking**: Small deposit required\n\nI can help you start this process right now. What's your moving date and location?"
  },
  services: {
    keywords: ['service', 'offer', 'provide', 'include', 'packing', 'storage', 'insurance'],
    response: "We offer comprehensive moving services:\n\n🚛 **Moving Services**\n• Local and long-distance moves\n• Residential and commercial\n• Same-day and scheduled moves\n\n📦 **Additional Services**\n• Professional packing/unpacking\n• Furniture disassembly/reassembly\n• Storage solutions\n• Moving insurance\n\nWhich service are you most interested in?"
  },
  contact: {
    keywords: ['contact', 'phone', 'email', 'support', 'help', 'talk', 'speak'],
    response: "Here are the best ways to reach us:\n\n📞 **Phone**: 1-800-MOVE-NOW\n📧 **Email**: support@movd.com\n💬 **Live Chat**: Right here!\n🕒 **Hours**: Monday-Sunday, 8AM-8PM\n\nFor urgent matters, calling is fastest. For general questions, I'm here to help 24/7! What can I assist you with?"
  },
  insurance: {
    keywords: ['insurance', 'protection', 'damage', 'broken', 'claim', 'coverage'],
    response: "We take great care of your belongings:\n\n🛡️ **Basic Coverage**: Included with every move\n💎 **Full Value Protection**: Optional comprehensive coverage\n📋 **Pre-Move Inventory**: Detailed documentation\n⚡ **Quick Claims**: Fast resolution process\n\nYour peace of mind is our priority. Would you like details about our protection options?"
  },
  preparation: {
    keywords: ['prepare', 'ready', 'checklist', 'pack', 'before', 'tips'],
    response: "Here's your moving preparation checklist:\n\n📋 **8 Weeks Before**\n• Get quotes and book your mover\n• Start decluttering\n\n📋 **4 Weeks Before**\n• Order packing supplies\n• Notify utilities and services\n\n📋 **1 Week Before**\n• Confirm moving details\n• Pack non-essentials\n\nWould you like a detailed timeline for your specific move date?"
  }
};

export default function LiveChatWidget() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1,
      from: 'bot', 
      text: "👋 Hi! I'm your AI moving assistant. I'm here to help with quotes, bookings, and any moving questions!",
      timestamp: new Date(),
      type: 'welcome'
    }
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [quickActionsVisible, setQuickActionsVisible] = useState(true);
  const [conversation, setConversation] = useState([]);
  const [userSatisfaction, setUserSatisfaction] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const generateAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Check for greetings
    if (/^(hi|hello|hey|good morning|good afternoon|good evening)/.test(message)) {
      return "Hello! Great to see you here. I'm ready to help with all your moving needs. What can I assist you with today?";
    }
    
    // Check for thanks
    if (/^(thank|thanks|appreciate)/.test(message)) {
      return "You're very welcome! I'm here whenever you need help with your move. Is there anything else I can assist you with?";
    }
    
    // Search knowledge base
    for (const data of Object.values(KNOWLEDGE_BASE)) {
      if (data.keywords.some(keyword => message.includes(keyword))) {
        return data.response;
      }
    }
    
    // Check for specific questions
    if (message.includes('how long') || message.includes('duration')) {
      return "Moving duration depends on several factors:\n\n• **Local moves**: 2-8 hours typically\n• **Long-distance**: 1-5 days\n• **Size matters**: Studio (2-3 hrs) vs 4+ bedroom (6-8 hrs)\n\nFor a more accurate estimate, I'd need to know your specific details. What size move are you planning?";
    }
    
    if (message.includes('tip') || message.includes('gratuity')) {
      return "Tipping is appreciated but not required:\n\n• **Standard**: $10-20 per mover for local moves\n• **Exceptional service**: $20-40 per mover\n• **Long-distance**: $40-80 per mover\n\nTips are based on service quality and your satisfaction level.";
    }
    
    // Default response with helpful suggestions
    return "I'd be happy to help! Here are some common topics I can assist with:\n\n• Getting moving quotes and pricing\n• Booking and scheduling moves\n• Available services and options\n• Moving preparation tips\n• Insurance and protection\n\nCould you tell me more about what you're looking for?";
  };

  const sendMessage = async (messageText = null) => {
    const userMessage = messageText || input.trim();
    if (!userMessage) return;

    // Add user message
    const userMsg = {
      id: Date.now(),
      from: 'user',
      text: userMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTyping(true);
    setQuickActionsVisible(false);
    
    // Simulate typing delay
    setTimeout(async () => {
      try {
        // Try AI API first, fall back to knowledge base
        let aiResponse;
        try {
          const res = await fetch('/api/ai-chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              message: userMessage,
              conversation: conversation.slice(-5) // Send last 5 messages for context
            }),
          });
          
          if (res.ok || typeof res.ok === 'undefined') {
            const data = await res.json();
            aiResponse = data.reply;
          }
        } catch {
          console.log('API not available, using knowledge base');
        }
        
        // Use knowledge base if API fails
        if (!aiResponse) {
          aiResponse = generateAIResponse(userMessage);
        }
        
        const botMsg = {
          id: Date.now() + 1,
          from: 'bot',
          text: aiResponse,
          timestamp: new Date(),
          actions: userMessage.toLowerCase().includes('quote') ? ['get_quote'] : null
        };
        
        setMessages(prev => [...prev, botMsg]);
        setConversation(prev => [...prev, userMsg, botMsg]);
        setTyping(false);
        scrollToBottom();
        
      } catch {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          from: 'bot',
          text: 'I apologize, but I\'m having trouble right now. Please try again in a moment or contact our support team directly.',
          timestamp: new Date(),
          error: true
        }]);
        setTyping(false);
      }
    }, 300 + Math.random() * 200); // Brief delay to simulate a response
  };

  const handleQuickAction = (action) => {
    sendMessage(action.query);
  };

  const handleSatisfaction = (rating) => {
    setUserSatisfaction(rating);
    const thankYouMsg = {
      id: Date.now(),
      from: 'bot',
      text: rating === 'positive' 
        ? "Thank you for the feedback! I'm glad I could help. Is there anything else you need assistance with?"
        : "Thanks for the feedback. I'd like to improve - could you tell me what I could have done better?",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thankYouMsg]);
  };

  const resetChat = () => {
    setMessages([
      { 
        id: 1,
        from: 'bot', 
        text: "👋 Hi! I'm your AI moving assistant. I'm here to help with quotes, bookings, and any moving questions!",
        timestamp: new Date(),
        type: 'welcome'
      }
    ]);
    setConversation([]);
    setQuickActionsVisible(true);
    setUserSatisfaction(null);
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
      {open ? (
        <Slide in={open} direction="up" timeout={300}>
          <Paper
            elevation={12}
            sx={{
              width: { xs: '90vw', sm: 380 },
              maxWidth: 400,
              height: { xs: '80vh', sm: 500 },
              borderRadius: 3,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    bgcolor: 'secondary.main',
                    color: 'primary.main' 
                  }}
                >
                  <SupportAgent />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    AI Assistant
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {typing ? 'Typing...' : 'Online • Ready to help'}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  onClick={resetChat}
                  aria-label="Reset chat"
                  sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
                  size="small"
                >
                  <Refresh />
                </IconButton>
                <IconButton 
                  onClick={() => setOpen(false)}
                  aria-label="Close live chat"
                  sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                p: 2,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {messages.map((msg) => (
                <Fade in key={msg.id} timeout={300}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    {msg.from === 'bot' && (
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: 'secondary.main',
                          color: 'primary.main',
                          fontSize: '0.875rem'
                        }}
                      >
                        🤖
                      </Avatar>
                    )}
                    
                    <Box
                      sx={{
                        bgcolor: msg.from === 'user' 
                          ? theme.palette.primary.main 
                          : 'white',
                        color: msg.from === 'user' ? 'white' : 'text.primary',
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        maxWidth: '75%',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        boxShadow: msg.from === 'user' 
                          ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                          : `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                        border: msg.from === 'bot' ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {msg.text}
                      
                      {/* Action buttons for bot messages */}
                      {msg.actions && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          {msg.actions.includes('get_quote') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open('/quote', '_blank')}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Get Quote
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Fade>
              ))}
              
              {/* Typing indicator */}
              {typing && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', color: 'primary.main' }}>
                    🤖
                  </Avatar>
                  <Box
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      AI is thinking...
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </Box>

            {/* Quick Actions */}
            {quickActionsVisible && (
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[50], 0.5) }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  How can we help?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Quick Actions:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {QUICK_ACTIONS.map((action) => (
                    <Chip
                      key={action.id}
                      label={action.label}
                      onClick={() => handleQuickAction(action)}
                      icon={action.icon}
                      size="small"
                      sx={{
                        bgcolor: 'white',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Input */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <InputBase
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Type your message..."
                multiline
                maxRows={3}
                sx={{
                  flex: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: 'white',
                  },
                }}
                inputProps={{ 'aria-label': 'Type your message' }}
              />
              <IconButton
                onClick={() => sendMessage()}
                aria-label="Send message"
                disabled={!input.trim() || typing}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                  '&:disabled': {
                    bgcolor: alpha(theme.palette.action.disabled, 0.3),
                  },
                }}
              >
                <Send />
              </IconButton>
            </Box>

            {/* Satisfaction */}
            {messages.length > 3 && !userSatisfaction && (
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[50], 0.5), borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  How am I doing?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleSatisfaction('positive')}
                    sx={{ color: 'success.main' }}
                  >
                    <ThumbUp fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleSatisfaction('negative')}
                    sx={{ color: 'error.main' }}
                  >
                    <ThumbDown fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Slide>
      ) : (
        <Fade in={!open} timeout={200}>
          <Badge 
            badgeContent="AI" 
            color="secondary"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                minWidth: 20,
                height: 20,
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.primary.main,
                fontWeight: 600,
              }
            }}
          >
            <IconButton
              onClick={() => setOpen(true)}
              aria-label="Open live chat"
              title="Open live chat"
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              <Chat sx={{ fontSize: 28 }} />
            </IconButton>
          </Badge>
        </Fade>
      )}
    </Box>
  );
}

// Remove or comment out unused third-party scripts, analytics, or widgets
